import React, { useCallback, useState } from "react";
import { Col, Divider, Modal, Row, Space, Spin } from "antd";
import { makeStyles } from "@material-ui/core/styles";
import CustomButton from "components/Common/buttons/CustomButton";
import { getCustomerGroupUploadUrl, updateCustomerGroup, uploadDataToS3BucketByUrl } from "utils/api";
import Title from "antd/lib/typography/Title";
import { defaultNgiErrorMethod, errorMessage, successMessage, warnMessage } from "components/Common/responses/message";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import closeIcon from "assets/images/svg/closeSmall.svg";
import Dragger from "antd/lib/upload/Dragger";
import uploadArea from "assets/images/svg/uploadArea.svg";
import * as xlsx from "xlsx";
import addCustomers from "assets/images/svg/add-customers.svg";
import removeCustomers from "assets/images/svg/remove-customers.svg";
import classnames from "classnames";
import CustomLink from "components/Common/buttons/CustomLink";
import { generateCSV } from "utils/helpers/functions";
import { CSV_TEMPLATES } from "utils/constants";

const useStyles = makeStyles({
  modal: {
    maxWidth: "900px!important",
  },
  infoContainer: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span:first-of-type": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  formItem: {
    display: "flex",

    "& .ant-col-12 + .ant-col-12": {
      paddingLeft: 10,
    },
  },
  disabled: {
    pointerEvents: "none",
    opacity: 0.6,
  },
  fileContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 15,
    border: "1px solid rgba(0, 0, 0, 0.05)",
    padding: "5px 10px",
    marginTop: 10,
  },
  removeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    background: "rgba(255, 255, 255, 1)",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  fileTitle: {
    fontWeight: 600,
    fontSize: 14,
  },
  infoMsgContainer: {
    display: "flex",
    padding: 15,
    marginBottom: 15,
    borderRadius: 5,
  },
  infoAdd: {
    backgroundColor: "#17C34714",
  },
  infoRemove: {
    backgroundColor: "#FF000014",
  },
  infoImg: {
    width: 32,
  },
  textAdd: {
    marginLeft: 15,
    "&> span": {
      textTransform: "uppercase",
      fontWeight: 600,
      color: "#17C347",
    },
  },
  textRemove: {
    marginLeft: 15,
    "&> span": {
      textTransform: "uppercase",
      fontWeight: 600,
      color: "#FF0000",
    },
  },
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
});

const AddRemoveCustomersModal = ({ data, onClose, update }) => {
  const classes = useStyles();
  const { item, type } = data;

  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadedData, setUploadedData] = useState("");

  const onSave = useCallback(async () => {
    if (!uploadedData) {
      warnMessage("Please upload file");
      return;
    }
    setIsLoading(true);
    const result = await getCustomerGroupUploadUrl().then(({ data: { result } }) =>
      uploadDataToS3BucketByUrl(result.url, uploadedData).then(() => result)
    );
    updateCustomerGroup({ ...item, customersListUpdateType: type, csvFilename: result.fileName }, item.id)
      .then(() => {
        successMessage(`Customers was ${type === "remove" ? "remove" : "added"} successfully`);
        setIsLoading(false);
        setTimeout(() => update && update(), 1500);
        onClose();
      })
      .catch(e => {
        setIsLoading(false);
        defaultNgiErrorMethod(e);
      });
  }, [uploadedData, type, data]);

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const readUploadFile = useCallback(e => {
    const file = e.file;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        const data = e.target.result;
        const workbook = xlsx.read(data);
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet, { raw: false });
        const csv = xlsx.utils.sheet_to_csv(worksheet, { raw: false });

        const formattedResult = json.map(row => ({ ...row }));
        const noError = formattedResult.every(item => item.customerNumber || null);
        !noError && errorMessage("Please upload the correct ‘*.csv | *.xls’ file with 'customerNumber' field");
        if (noError) {
          setUploadedData(csv);
          setFile(file);
        } else {
          setUploadedData("");
          setFile(null);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const removeFile = useCallback(() => {
    setFile(null);
    setUploadedData([]);
  }, []);

  return (
    <Modal
      title={
        <div className={classes.infoContainer}>
          {type === "remove" ? "Remove" : "Add"} Customers
          <span>Please fill in all mandatory fields</span>
        </div>
      }
      className={classes.modal}
      open
      onCancel={onClose}
      closable={false}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <div className={classnames(classes.infoMsgContainer, type === "remove" ? classes.infoRemove : classes.infoAdd)}>
          <img
            className={classes.infoImg}
            src={type === "remove" ? removeCustomers : addCustomers}
            alt="Customer remove/add"
          />
          <div className={type === "remove" ? classes.textRemove : classes.textAdd}>
            <span>IMPORTANT:</span> Upload a list of customers to be removed from this customer group—this action does
            not add customers!
          </div>
        </div>
        <Row className={classes.formItem}>
          <Col span={24}>
            <div className={classes.titleContainer}>
              <Title level={5}>{type === "remove" ? "Removed" : "New"} Customers List (Supports *.CSV, *.XLS)*</Title>
              <CustomLink color="#0085FF" onClick={() => generateCSV(CSV_TEMPLATES.ADD_REMOVE_CUSTOMERS_GROUP)}>
                Download *.csv template
              </CustomLink>
            </div>

            {file ? (
              <div className={classes.fileContainer}>
                <div className={classes.fileTitle}>{`${file.name}`}</div>
                <div onClick={removeFile} className={classes.removeBtn}>
                  <img src={closeIcon} alt="remove" />
                </div>
              </div>
            ) : (
              <Dragger accept=".csv,.xls" showUploadList={false} customRequest={readUploadFile} name="file">
                <img className="upload-bg" src={uploadArea} alt="upload" />
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">
                  Support for a single upload. Strictly prohibit from uploading company data or other band files
                </p>
              </Dragger>
            )}
          </Col>
        </Row>
      </Spin>
      <Divider />
      <br />
      <Space>
        <CustomButton onClick={onClose} type="primary" size="small" color="outlined" text="Cancel" />
        <CustomButton type="primary" onClick={openConfirmationModal} text="CONFIRM" size="small" />
      </Space>

      {isConfirmationOpen && (
        <ConfirmationModal
          description={`Are you sure you want to ${type === "remove" ? "remove" : "add"} customers?`}
          onCancel={closeConfirmationModal}
          onConfirm={onSave}
          loading={isLoading}
        />
      )}
    </Modal>
  );
};

export default AddRemoveCustomersModal;
